import type * as TF from 'type-fest';

import type { RouteMatch } from '@tanstack/react-router';
import { type MetaTagProps, type SEOPayload, seo } from '#/lib/head';
import type { DangerouslyAllowAny } from '#/types';

export interface PageSEOPayload {
  seo: SEOPayload;
}

export type SEODefaults = TF.SetRequired<SEOPayload, 'title' | 'description' | 'keywords'>;

/**
 * Generates meta tags for a page, with required defaults
 *
 * @param page A page payload
 * @param defaults The default SEO values
 * @returns An array of meta tags
 */
export function pageSEO(
  defaults: SEODefaults,
  match: PageSEOMatch,
  page?: PageSEOPayload
): MetaTagProps[] {
  return seo({
    title: createTitle(page?.seo.title ?? defaults.title),
    description: page?.seo.description ?? defaults.description,
    keywords: page?.seo.keywords ?? defaults.keywords,
    image: page?.seo.image,
    url: match.pathname,
  });
}

/*
|------------------
| Internals
|------------------
*/

type PageSEOMatch = RouteMatch<
  DangerouslyAllowAny,
  DangerouslyAllowAny,
  DangerouslyAllowAny,
  DangerouslyAllowAny,
  DangerouslyAllowAny,
  DangerouslyAllowAny,
  DangerouslyAllowAny
>;

/**
 * Creates a title with the suffix
 *
 * @param title The title of the page
 * @returns A title with the suffix
 */
function createTitle(title: string): string {
  return `${title.replace(titleSuffix, '')} · Joggr`;
}

const titleSuffix = ' · Joggr';
